import React, { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'

import { ink } from './adminTokens'

export type SectionStatus = 'pending' | 'in_progress' | 'complete'

interface SectionWrapperProps {
  title: string
  status: SectionStatus
  /** Right-aligned in the card header — buttons that act on this card alone. */
  actions?: ReactNode
  children: ReactNode
}

// A card inside a pane. The props are unchanged from when this wrapped a
// standalone section, so no section component had to be touched to move it
// into AdminShell.
//
// What did change: 'pending' and 'complete' no longer render a chip. Ten cards
// each announcing "Complete" was most of the old screen's noise, and the rail
// now carries each group's state. A running job still shows, because that is
// the one status a viewer cannot infer from the card's own contents.
const SectionWrapper: FC<SectionWrapperProps> = ({ title, status, actions, children }) => (
  <Box
    sx={{
      bgcolor: ink.card,
      border: `1px solid ${ink.rule}`,
      borderRadius: '8px',
      p: '18px 20px 20px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
      <Typography component="h2" variant="h6">
        {title}
      </Typography>
      {status === 'in_progress' && (
        <Chip
          label="Running"
          size="small"
          icon={<CircularProgress size={12} sx={{ ml: '6px !important' }} />}
          sx={{
            height: 24,
            bgcolor: ink.neutralTint,
            color: ink.secondary,
            '& .MuiChip-label': { fontSize: '0.71875rem' },
          }}
        />
      )}
      {actions && (
        <>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{actions}</Box>
        </>
      )}
    </Box>
    {children}
  </Box>
)

export default SectionWrapper
